/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import { adminGlass } from "../../styles/adminGlassTokens";
import { FadeUpStagger, MotionCard, MotionButton } from "../motion/AdminMotion";

const FALLBACK_ATTENTION_ROWS = [
  {
    label: "No registers requiring attention",
    sub: "All register workflows look clear",
    status: "Clear",
    tone: "#84c441",
  },
];

const QUICK_ACTIONS = [
  { key: "students", label: "Open students" },
  { key: "classes", label: "Open classes" },
  { key: "teachers", label: "Review teachers" },
  { key: "parents", label: "Manage parents" },
  { key: "parent-invites", label: "Parent invites" },
  { key: "attendance-control", label: "Attendance control" },
];

function formatPercent(value) {
  return `${Number(value || 0)}%`;
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function getStatusPillStyle(status) {
  const value = String(status || "").toLowerCase();

  if (value === "missing") {
    return { color: "#b42318", background: "#fee4e2", border: "1px solid #fecdca" };
  }

  if (value === "incomplete") {
    return { color: "#b54708", background: "#fef0c7", border: "1px solid #fedf89" };
  }

  if (value === "late") {
    return { color: "#175cd3", background: "#dbeafe", border: "1px solid #bfdcff" };
  }

  return { color: "#027a48", background: "#dcfae6", border: "1px solid #abefc6" };
}

function getAttentionAction(item) {
  const status = String(item?.status || "").toLowerCase();

  if (status === "clear") return null;

  return {
    tab: "attendance-control",
    status: "issues",
    dateKey: item?.dateKey || getISODate(new Date()),
    classId: item?.classId || "",
    registerId: item?.registerId || "",
  };
}

function openAttentionItem(item, onNavigate) {
  const action = getAttentionAction(item);
  if (!action) return;

  if (typeof onNavigate === "function") {
    onNavigate(action.tab, {
      status: action.status,
      dateKey: action.dateKey,
      classId: action.classId,
      registerId: action.registerId,
    });
    return;
  }

  const params = new URLSearchParams({
    tab: action.tab,
    status: action.status,
    dateKey: action.dateKey,
  });

  if (action.classId) params.set("classId", action.classId);
  if (action.registerId) params.set("registerId", action.registerId);

  window.location.href = `/admin-manager?${params.toString()}`;
}

function clampPercent(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function averageTrend(points = []) {
  const valid = points.filter((x) => Number(x?.value || 0) > 0);
  if (!valid.length) return 0;

  return Math.round(
    valid.reduce((sum, item) => sum + Number(item?.value || 0), 0) / valid.length
  );
}

function getISODate(date) {
  const local = new Date(date);
  local.setHours(12, 0, 0, 0);
  const year = local.getFullYear();
  const month = String(local.getMonth() + 1).padStart(2, "0");
  const day = String(local.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getRegisterDate(register) {
  const value =
    register.date ||
    register.dateKey ||
    register.attendanceDate ||
    register.createdAt?.toDate?.()?.toISOString?.()?.slice(0, 10);

  return String(value || "").slice(0, 10);
}

function isWeekday(date) {
  const day = date.getDay();
  return day !== 0 && day !== 6;
}

function TrendLineChart({
  points = [],
  range = "week",
  onRangeChange,
  loading = false,
}) {
  const safePoints = Array.isArray(points) && points.length ? points : [];
  const chartPoints = safePoints.length
    ? safePoints
    : [
        { label: "Mon", value: 0 },
        { label: "Tue", value: 0 },
        { label: "Wed", value: 0 },
        { label: "Thu", value: 0 },
        { label: "Fri", value: 0 },
      ];

  const width = 760;
  const height = 260;
  const padding = { top: 22, right: 28, bottom: 44, left: 54 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const yTicks = [100, 95, 90, 85];

  function xFor(index) {
    if (chartPoints.length <= 1) return padding.left + plotWidth / 2;
    return padding.left + (index / (chartPoints.length - 1)) * plotWidth;
  }

  function yFor(value) {
    const bounded = Math.max(80, Math.min(100, Number(value || 0)));
    return padding.top + ((100 - bounded) / 20) * plotHeight;
  }

  const realPath = chartPoints
    .map((point, index) => `${index === 0 ? "M" : "L"} ${xFor(index)} ${yFor(point.value)}`)
    .join(" ");

  const targetPath = chartPoints
    .map((_, index) => `${index === 0 ? "M" : "L"} ${xFor(index)} ${yFor(95)}`)
    .join(" ");

  const activeAverage = averageTrend(chartPoints);
  const labelLimit = range === "month" ? 10 : 5;

  return (
    <div style={styles.lineChartCard}>
      <div style={styles.lineChartHeader}>
        <div>
          <div style={styles.panelEyebrow}>
            {range === "month" ? "School month view" : "School week view"}
          </div>
          <h4 style={styles.trendChartTitle}>Attendance overview</h4>
        </div>

        <div style={styles.lineChartActions}>
          <div style={styles.lineLegend}>
            <span>
              <b style={{ background: "#2563eb" }} />
              Attendance
            </span>
            <span>
              <b style={{ background: "#7c3aed" }} />
              95% guide
            </span>
          </div>

          <select
            value={range}
            onChange={(e) => onRangeChange?.(e.target.value)}
            style={styles.rangeSelect}
            aria-label="Attendance chart range"
          >
            <option value="week">This week</option>
            <option value="month">This month</option>
          </select>
        </div>
      </div>

      <div style={styles.lineChartAverage}>
        <strong style={styles.lineChartAverageValue}>
          {loading ? "—" : `${activeAverage}%`}
        </strong>
        <span style={styles.lineChartAverageLabel}>
          {range === "month" ? "Month average" : "Week average"}
        </span>
      </div>

      <div style={styles.lineChartWrap}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={styles.lineChartSvg}
          role="img"
          aria-label="Attendance percentage line chart"
        >
          <defs>
            <linearGradient id="ppAttendanceLine" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#2563eb" />
              <stop offset="100%" stopColor="#38bdf8" />
            </linearGradient>
          </defs>

          {yTicks.map((tick) => (
            <g key={tick}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={yFor(tick)}
                y2={yFor(tick)}
                stroke="#e8eef8"
                strokeWidth="1"
                strokeDasharray={tick === 100 ? "0" : "7 7"}
              />
              <text
                x={padding.left - 18}
                y={yFor(tick) + 4}
                textAnchor="end"
                fontSize="12"
                fontWeight="800"
                fill="#6b7a99"
              >
                {tick}%
              </text>
            </g>
          ))}

          <path
            d={targetPath}
            fill="none"
            stroke="#7c3aed"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.72"
          />

          <path
            d={realPath}
            fill="none"
            stroke="url(#ppAttendanceLine)"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {chartPoints.map((point, index) => (
            <g key={`${point.label}-${index}`}>
              <circle
                cx={xFor(index)}
                cy={yFor(point.value)}
                r="6"
                fill="#2563eb"
                stroke="#ffffff"
                strokeWidth="3"
              />
              {(range === "week" ||
                index === 0 ||
                index === chartPoints.length - 1 ||
                index % Math.ceil(chartPoints.length / labelLimit) === 0) && (
                <text
                  x={xFor(index)}
                  y={height - 14}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="800"
                  fill="#6b7a99"
                >
                  {point.label}
                </text>
              )}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

export default function OverviewSection({ schoolId, onNavigate }) {
  const currentSchoolId = schoolId || null;
  const [loading, setLoading] = useState(true);

  const [metrics, setMetrics] = useState({
    students: 0,
    classes: 0,
    behaviourLogs: 0,
    teachers: 0,
    parents: 0,
    registers: 0,
    attendanceRate: 0,
    presentToday: 0,
    absentToday: 0,
    lateToday: 0,
  });

  const [intel, setIntel] = useState({
    pendingTeacherInvites: 0,
    pendingParentInvites: 0,
    registersToReview: 0,
    attendanceFollowUps: 0,
    attentionRows: FALLBACK_ATTENTION_ROWS,
    trendPoints: [
      { label: "Mon", value: 88 },
      { label: "Tue", value: 90 },
      { label: "Wed", value: 89 },
      { label: "Thu", value: 92 },
      { label: "Fri", value: 91 },
    ],
    trendWeekPoints: [
      { label: "Mon", value: 88 },
      { label: "Tue", value: 90 },
      { label: "Wed", value: 89 },
      { label: "Thu", value: 92 },
      { label: "Fri", value: 91 },
    ],
    trendMonthPoints: [],
    trendAverage: 91,
    trendWeekAverage: 90,
    trendMonthAverage: 0,
    recentActivity: [],
  });

  const [chartRange, setChartRange] = useState("week");

  useEffect(() => {
    let alive = true;

    async function loadOverview() {
      if (!currentSchoolId) {
        if (!alive) return;
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const base = ["schools", currentSchoolId];

const [
  studentsSnap,
  classesSnap,
  staffSnap,
  parentsSnap,
  invitesSnap,
  auditSnap,
  behaviourSnap,
  attendanceSnap,
  liveRegistersSnap,
] = await Promise.all([
  getDocs(collection(db, ...base, "students")),
  getDocs(collection(db, ...base, "classes")),
  getDocs(collection(db, ...base, "staff")),
  getDocs(collection(db, ...base, "parents")),
  getDocs(collection(db, ...base, "invites")),
  getDocs(collection(db, ...base, "auditLogs")),
  getDocs(collection(db, ...base, "behaviourLogs")),
  getDocs(collection(db, ...base, "attendance")),
  getDocs(collection(db, ...base, "liveRegisters")),
]);
        if (!alive) return;

        const students = studentsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const classes = classesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const parents = parentsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const staff = staffSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const invites = invitesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const audits = auditSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const teachers = staff.filter((x) => {
          const role = String(x.role || "").toLowerCase();
          return role === "teacher" || role === "staff";
        });

        const admins = staff.filter((x) => {
          const role = String(x.role || "").toLowerCase();
          return role === "admin" || role === "manager";
        });

        let pendingTeacherInvites = 0;
        let pendingParentInvites = 0;

        invites.forEach((item) => {
          const status = String(item.status || "").toLowerCase();
          const roleValue = String(item.role || "").toLowerCase();

          if (status && status !== "pending") return;

          if (roleValue.includes("teacher") || roleValue.includes("staff")) {
            pendingTeacherInvites += 1;
          } else if (roleValue.includes("parent")) {
            pendingParentInvites += 1;
          }
        });

        const recentActivity = audits
          .sort((a, b) => {
            const at = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const bt = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return bt - at;
          })
          .slice(0, 30)
          .map((item) => ({
            id: item.id,
            title: String(item.action || "system_update")
              .replaceAll("_", " ")
              .replace(/\b\w/g, (m) => m.toUpperCase()),
            sub:
              item.metadata?.targetName ||
              item.metadata?.targetId ||
              item.adminEmail ||
              "PresentPlus activity",
            time: item.createdAt?.toDate
              ? item.createdAt.toDate().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—",
          }));

const liveRegisters = liveRegistersSnap.docs.map((d) => ({
  id: d.id,
  ...d.data(),
}));

const todayKey = getISODate(new Date());

function normaliseStatus(value) {
  const v = String(value || "")
    .toLowerCase()
    .trim();

  // PRESENT
  if (
    [
      "present",
      "p",
      "in",
      "attended",
      "present_am",
      "present_pm",
      "/",
      "\\",
      "✓",
      "yes",
    ].includes(v)
  ) {
    return "present";
  }

  // LATE
  if (
    [
      "late",
      "l",
      "late_present",
    ].includes(v)
  ) {
    return "late";
  }

  // ABSENT
  if (
    [
      "absent",
      "a",
      "unauthorised",
      "unauthorized",
      "authorised",
      "authorized",
      "missing",
      "no",
    ].includes(v)
  ) {
    return "absent";
  }

  return "";
}

function getRegisterRows(register) {
  // direct arrays
  if (Array.isArray(register.rows)) return register.rows;
  if (Array.isArray(register.students)) return register.students;
  if (Array.isArray(register.attendance)) return register.attendance;
  if (Array.isArray(register.marks)) return register.marks;

  // object maps -> convert to array
  if (
    register.students &&
    typeof register.students === "object" &&
    !Array.isArray(register.students)
  ) {
    return Object.values(register.students);
  }

  if (
    register.marks &&
    typeof register.marks === "object" &&
    !Array.isArray(register.marks)
  ) {
    return Object.values(register.marks);
  }

  if (
    register.attendanceData &&
    typeof register.attendanceData === "object"
  ) {
    return Object.values(register.attendanceData);
  }

  if (
    register.entries &&
    typeof register.entries === "object"
  ) {
    return Object.values(register.entries);
  }

  return [];
}

const todayLiveRegisters = liveRegisters.filter((item) => {
  const dateValue =
    item.date ||
    item.dateKey ||
    item.attendanceDate ||
    item.createdAt?.toDate?.()?.toISOString?.()?.slice(0, 10);

  return String(dateValue || "").slice(0, 10) === todayKey;
});

let presentToday = 0;
let lateToday = 0;
let absentToday = 0;

const activeStudentCount = students.filter(
  (x) => !x.deletedAt
).length;

let registersToReview = 0;
let missingMarks = 0;

const attentionRows = [];

todayLiveRegisters.forEach((register) => {
  const totals = register.totals || {};

  const present = Number(totals.present || 0);
  const absent = Number(totals.absent || 0);
  const late = Number(totals.late || 0);
  const total = Number(totals.total || 0);
  const marked = Number(totals.marked || 0);

  presentToday += present;
  absentToday += absent;
  lateToday += late;


  const missing = Math.max(total - marked, 0);

  if (
    missing > 0 ||
    String(register.status || "").toLowerCase() !== "submitted"
  ) {
    registersToReview += 1;

    attentionRows.push({
      label: "Register data needs review",
      sub:
        missing > 0
          ? `${missing} missing attendance mark${
              missing === 1 ? "" : "s"
            } found today`
          : "Register has not been fully submitted",
      status: "Incomplete",
      tone: "#f79009",
      dateKey: todayKey,
      classId: register.classId || register.classID || "",
      registerId: register.id || "",
    });

    missingMarks += missing;
  }
});

const attendanceRate =
  activeStudentCount > 0
    ? Math.min(
        100,
        Math.round(
          ((presentToday + lateToday) / activeStudentCount) * 100
        )
      )
    : 0;
/* =========================================
   WEEK + MONTH ATTENDANCE TREND
========================================= */

function calculateAttendancePoint(dateKey, label) {
  const dayRegisters = liveRegisters.filter((item) => getRegisterDate(item) === dateKey);

  let dayPresent = 0;
  let dayLate = 0;

  dayRegisters.forEach((register) => {
    const totals = register.totals || {};

    dayPresent += Number(totals.present || 0);
    dayLate += Number(totals.late || 0);
  });

  const percentage =
    activeStudentCount > 0
      ? clampPercent(((dayPresent + dayLate) / activeStudentCount) * 100)
      : 0;

  return {
    label,
    value: percentage,
    dateKey,
  };
}

const todayForTrend = new Date();
const weekStart = new Date(todayForTrend);
const dayOfWeek = todayForTrend.getDay(); // Sun=0, Mon=1, Tue=2...
const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

weekStart.setHours(12, 0, 0, 0);
weekStart.setDate(todayForTrend.getDate() - daysSinceMonday);

const weekDays = [];

for (let i = 0; i < 5; i++) {
  const date = new Date(weekStart);
  date.setDate(weekStart.getDate() + i);
  weekDays.push(date);
}

const trendWeekPoints = weekDays.map((date) =>
  calculateAttendancePoint(
    getISODate(date),
    date.toLocaleDateString("en-GB", { weekday: "short" })
  )
);

const monthStart = new Date(todayForTrend.getFullYear(), todayForTrend.getMonth(), 1);
monthStart.setHours(12, 0, 0, 0);

const monthDays = [];
const cursorDate = new Date(monthStart);

while (cursorDate <= todayForTrend) {
  if (isWeekday(cursorDate)) {
    monthDays.push(new Date(cursorDate));
  }

  cursorDate.setDate(cursorDate.getDate() + 1);
}

const trendMonthPoints = monthDays.map((date) =>
  calculateAttendancePoint(
    getISODate(date),
    date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
  )
);

const trendPoints = trendWeekPoints;
const trendAverage = averageTrend(trendWeekPoints);
const trendWeekAverage = averageTrend(trendWeekPoints);
const trendMonthAverage = averageTrend(trendMonthPoints);

const attendanceFollowUps = absentToday + lateToday;

const finalAttentionRows =
  attentionRows.length > 0
    ? attentionRows
    : FALLBACK_ATTENTION_ROWS;

setMetrics({
  students: students.filter((x) => !x.deletedAt).length,
  classes: classes.filter((x) => !x.deletedAt).length,
  parents: parents.filter((x) => !x.deletedAt).length,
  admins: admins.filter((x) => !x.deletedAt).length,
  teachers: teachers.filter((x) => !x.deletedAt).length,
  behaviourLogs: behaviourSnap.docs.length,

  registers: liveRegisters.length,

  attendanceRate,
  presentToday,
  absentToday,
  lateToday,
});

setIntel({
  pendingTeacherInvites,
  pendingParentInvites,
  registersToReview,
 attendanceFollowUps,
          attentionRows: finalAttentionRows,
trendPoints,
trendWeekPoints,
trendMonthPoints,
trendAverage,
trendWeekAverage,
trendMonthAverage,
          recentActivity,
        });
      } catch (err) {
        console.error("Failed to load overview:", err);

        if (!alive) return;

        setIntel((prev) => ({
          ...prev,
          attentionRows: FALLBACK_ATTENTION_ROWS,
          recentActivity: [],
        }));
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadOverview();

    return () => {
      alive = false;
    };
  }, [currentSchoolId]);

  const summaryCards = useMemo(
    () => [
      { label: "Students", value: formatCount(metrics.students), sub: "Active student records", tone: "blue", action: "students" },
      { label: "Classes", value: formatCount(metrics.classes), sub: "Structured class groups", tone: "violet", action: "classes" },
      { label: "Teachers", value: formatCount(metrics.teachers), sub: "School teaching staff", tone: "green", action: "teachers" },
      { label: "Parents", value: formatCount(metrics.parents), sub: "Linked parent records", tone: "rose", action: "parents" },
      {
  label: "Behaviour Logs",
  value: formatCount(metrics.behaviourLogs || 0),
  sub: "Live behaviour records",
  tone: "violet",
  action: "behaviour-dashboard",
},
    ],
    [metrics]
  );

  const opsCards = useMemo(
    () => [
      {
        label: "Attendance today",
        value: formatPercent(metrics.attendanceRate),
        sub: `${formatCount(metrics.presentToday)} present today`,
        tone: "primary",
      },
      {
        label: "Registers to review",
        value: formatCount(intel.registersToReview),
        sub: "Registers needing admin attention",
        tone: "warning",
      },
      {
        label: "Attendance follow-ups",
        value: formatCount(intel.attendanceFollowUps),
        sub: `${formatCount(metrics.absentToday)} absent · ${formatCount(metrics.lateToday)} late`,
        tone: "warningSoft",
      },
      {
        label: "Pending invites",
        value: formatCount(intel.pendingTeacherInvites + intel.pendingParentInvites),
        sub: `${formatCount(intel.pendingTeacherInvites)} teacher · ${formatCount(intel.pendingParentInvites)} parent`,
        tone: "mint",
      },
    ],
    [intel, metrics]
  );


  const selectedTrendPoints =
    chartRange === "month"
      ? intel.trendMonthPoints || []
      : intel.trendWeekPoints || intel.trendPoints || [];

  const selectedTrendAverage =
    chartRange === "month"
      ? intel.trendMonthAverage || averageTrend(selectedTrendPoints)
      : intel.trendWeekAverage || averageTrend(selectedTrendPoints);

  return (
    <div style={styles.page}>
      <MotionCard style={styles.hero} hover>
        <div style={styles.heroLeft}>
          <div style={styles.heroEyebrow}>PresentPlus Admin</div>
          <h2 style={styles.heroTitle}>Overview Dashboard</h2>
          <p style={styles.heroText}>
            Track school operations, attendance pressure points, onboarding flow,
            and what needs attention next.
          </p>
        </div>

        <div style={styles.heroBadge}>
          <div style={styles.heroBadgeLabel}>Selected attendance average</div>
          <div style={styles.heroBadgeValue}>{formatPercent(selectedTrendAverage)}</div>
        </div>
      </MotionCard>

      <section style={styles.gridFive}>
        {summaryCards.map((card) => (
          <MotionButton
            key={card.label}
            onClick={() => onNavigate?.(card.action)}
            style={{
              ...styles.statCard,
              ...cardToneStyles[card.tone],
              cursor: onNavigate ? "pointer" : "default",
              textAlign: "left",
              border: "none",
            }}
          >
            <div style={styles.statLabel}>{card.label}</div>
            <div
              style={{
                ...styles.statValue,
                color: cardToneStyles[card.tone]?.valueColor || "#112042",
              }}
            >
              {loading ? "—" : card.value}
            </div>
            <div style={styles.statSub}>{card.sub}</div>
          </MotionButton>
        ))}
      </section>

      <section style={styles.gridFourBalanced}>
        {opsCards.map((card) => {
          const isReviewCard = card.label === "Registers to review";
          const hasReviewItems = Number(intel.registersToReview || 0) > 0;

          return (
            <MotionButton
              key={card.label}
              type="button"
              onClick={() => {
                if (isReviewCard && hasReviewItems) {
                  onNavigate?.("attendance-control", {
                    status: "issues",
                    dateKey: getISODate(new Date()),
                  });
                }
              }}
              style={{
                ...styles.statCard,
                ...cardToneStyles[card.tone],
                ...(isReviewCard && hasReviewItems
                  ? styles.clickableCard
                  : styles.passiveCard),
              }}
            >
              <div style={styles.statLabel}>{card.label}</div>
              <div
                style={{
                  ...styles.statValue,
                  color: cardToneStyles[card.tone]?.valueColor || "#112042",
                }}
              >
                {loading ? "—" : card.value}
              </div>
              <div style={styles.statSub}>{card.sub}</div>

              {isReviewCard && hasReviewItems ? (
                <div style={styles.cardActionText}>Open review queue →</div>
              ) : null}
            </MotionButton>
          );
        })}
      </section>

      <section style={styles.mainGrid}>
        <MotionCard style={styles.panelLarge} hover>
          <div style={styles.panelHead}>
            <div>
              <div style={styles.panelEyebrow}>Performance</div>
              <h3 style={styles.panelTitle}>Attendance trend</h3>
            </div>
            <div style={styles.inlineMetric}>
              <span style={styles.inlineMetricLabel}>Today</span>
              <span style={styles.inlineMetricValue}>
                {loading ? "—" : formatPercent(metrics.attendanceRate)}
              </span>
            </div>
          </div>

          <div style={styles.trendPanelInner}>
            <TrendLineChart
              points={selectedTrendPoints}
              range={chartRange}
              onRangeChange={setChartRange}
              loading={loading}
            />
          </div>
        </MotionCard>

        <MotionCard style={styles.panelSide} hover>
          <div style={styles.panelHead}>
            <div>
              <div style={styles.panelEyebrow}>Actions</div>
              <h3 style={styles.panelTitle}>Quick access</h3>
            </div>
          </div>

          <div style={styles.quickList}>
            <FadeUpStagger delay={0.12} gap={0.04}>
              {QUICK_ACTIONS.map((item) => (
                <MotionButton
                  key={item.key}
                  onClick={() => onNavigate?.(item.key)}
                  style={styles.quickAction}
                >
                  <span>{item.label}</span>
                  <span style={styles.quickArrow}>→</span>
                </MotionButton>
              ))}
            </FadeUpStagger>
          </div>
        </MotionCard>
      </section>

      <section style={styles.bottomGrid}>
        <MotionCard style={styles.reviewPanel}>
          <div style={styles.panelHead}>
            <div>
              <div style={styles.panelEyebrow}>Attention</div>
              <h3 style={styles.panelTitle}>Registers requiring review</h3>
            </div>
          </div>

          <div style={styles.scrollArea}>
            <div style={styles.attentionList}>
              {(intel.attentionRows || []).map((item, index) => {
                const pillStyle = getStatusPillStyle(item.status);
                const hasAction = !!getAttentionAction(item);

                return (
                  <MotionButton
                    key={`${item.label}-${index}`}
                    type="button"
                    onClick={() => openAttentionItem(item, onNavigate)}
                    style={{
                      ...styles.attentionRow,
                      ...(hasAction ? styles.attentionRowClickable : {}),
                    }}
                    disabled={!hasAction}
                  >
                    <div style={styles.attentionLeft}>
                      <div
                        style={{
                          ...styles.attentionDot,
                          background: item.tone || "#84c441",
                        }}
                      />
                      <div>
                        <div style={styles.attentionLabel}>{item.label}</div>
                        <div style={styles.attentionSub}>{item.sub}</div>
                        {hasAction ? (
                          <div style={styles.attentionActionText}>
                            Open Attendance Control →
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div style={{ ...styles.statusPill, ...pillStyle }}>{item.status}</div>
                  </MotionButton>
                );
              })}
            </div>
          </div>
        </MotionCard>

        <MotionCard style={styles.activityPanel}>
          <div style={styles.panelHead}>
            <div>
              <div style={styles.panelEyebrow}>Audit</div>
              <h3 style={styles.panelTitle}>Recent activity</h3>
            </div>
          </div>

          <div style={styles.scrollArea}>
            <div style={styles.activityList}>
              {(intel.recentActivity || []).length ? (
                intel.recentActivity.map((item) => (
                  <div key={item.id} style={styles.activityRow}>
                    <div style={styles.activityMeta}>
                      <div style={styles.activityTitle}>{item.title}</div>
                      <div style={styles.activitySub}>{item.sub}</div>
                    </div>
                    <div style={styles.activityTime}>{item.time}</div>
                  </div>
                ))
              ) : (
                <div style={styles.emptyState}>No recent activity found yet.</div>
              )}
            </div>
          </div>
        </MotionCard>
      </section>
    </div>
  );
}

const cardToneStyles = {
  blue: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(238,243,255,0.99) 100%)",
    border: "1px solid rgba(191,220,255,0.9)",
    boxShadow: "0 20px 40px rgba(79,99,255,0.10)",
    valueColor: "#2845d9",
  },
  violet: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(247,244,255,0.99) 100%)",
    border: "1px solid rgba(230,220,255,0.9)",
    boxShadow: "0 20px 40px rgba(111,89,255,0.08)",
    valueColor: "#6f59ff",
  },
  green: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(238,252,246,0.99) 100%)",
    border: "1px solid rgba(171,239,198,0.9)",
    boxShadow: "0 20px 40px rgba(2,122,72,0.08)",
    valueColor: "#027a48",
  },
  rose: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(255,245,248,0.99) 100%)",
    border: "1px solid rgba(254,205,202,0.88)",
    boxShadow: "0 20px 40px rgba(180,35,24,0.08)",
    valueColor: "#b42318",
  },
  primary: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(238,243,255,0.99) 100%)",
    border: "1px solid rgba(191,220,255,0.9)",
    boxShadow: "0 20px 40px rgba(79,99,255,0.10)",
    valueColor: "#2845d9",
  },
  warning: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(255,247,236,0.99) 100%)",
    border: "1px solid rgba(254,223,137,0.88)",
    boxShadow: "0 20px 40px rgba(181,71,8,0.08)",
    valueColor: "#b54708",
  },
  warningSoft: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(255,250,242,0.99) 100%)",
    border: "1px solid rgba(255,231,194,0.9)",
    boxShadow: "0 20px 40px rgba(181,71,8,0.06)",
    valueColor: "#b54708",
  },
  mint: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(238,252,246,0.99) 100%)",
    border: "1px solid rgba(171,239,198,0.9)",
    boxShadow: "0 20px 40px rgba(2,122,72,0.08)",
    valueColor: "#027a48",
  },
};

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 22,
    position: "relative",
    isolation: "isolate",
  },

  hero: {
    borderRadius: 28,
    padding: "30px 28px",
    background: "var(--pp-brand-gradient)",
    color: "#ffffff",
    boxShadow: "0 28px 70px rgba(79,99,255,0.18)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
  },

  heroLeft: { minWidth: 0 },

  heroEyebrow: {
    fontSize: 12,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    fontWeight: 800,
    opacity: 0.86,
    marginBottom: 10,
  },

  heroTitle: {
    margin: 0,
    fontSize: 42,
    lineHeight: 1.04,
    letterSpacing: "-0.03em",
    fontWeight: 900,
  },

  heroText: {
    margin: "12px 0 0",
    fontSize: 16,
    lineHeight: 1.65,
    opacity: 0.95,
    maxWidth: 760,
  },

  heroBadge: {
    minWidth: 220,
    borderRadius: 24,
    padding: "18px 20px",
    background: "rgba(255,255,255,0.14)",
    border: "1px solid rgba(255,255,255,0.18)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    boxShadow: "0 16px 36px rgba(18,28,80,0.16)",
  },

  heroBadgeLabel: {
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.82,
    marginBottom: 8,
    fontWeight: 700,
  },

  heroBadgeValue: {
    fontSize: 34,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "-0.03em",
  },

  gridFive: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 18,
    alignItems: "stretch",
  },

  gridFourBalanced: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 18,
    alignItems: "stretch",
  },

  statCard: {
    width: "100%",
    minHeight: 150,
    borderRadius: 28,
    padding: "24px 24px",
    boxShadow:
      "0 26px 64px rgba(17,32,66,0.09), inset 0 1px 0 rgba(255,255,255,0.78)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    boxSizing: "border-box",
  },

  statLabel: {
    fontSize: 12,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    fontWeight: 800,
    color: "#5d6b8a",
    marginBottom: 12,
  },

  statValue: {
    fontSize: 42,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "-0.04em",
  },

  statSub: {
    marginTop: 10,
    fontSize: 16,
    lineHeight: 1.55,
    color: "#667085",
  },

  clickableCard: {
    border: "1px solid rgba(254, 223, 137, 0.95)",
    cursor: "pointer",
    textAlign: "left",
  },

  passiveCard: {
    border: "1px solid transparent",
    cursor: "default",
    textAlign: "left",
  },

  cardActionText: {
    marginTop: 14,
    fontSize: 13,
    fontWeight: 950,
    color: "#b54708",
  },

  mainGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.35fr) minmax(300px, 0.78fr)",
    gap: 18,
    alignItems: "start",
    minWidth: 0,
  },

  bottomGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 18,
  },

  panelLarge: {
    ...adminGlass.panel,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  },

  panelSide: {
    ...adminGlass.panel,
    minWidth: 0,
  },

  reviewPanel: {
    ...adminGlass.panel,
    height: 560,
    maxHeight: 560,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  activityPanel: {
    ...adminGlass.panel,
    height: 560,
    maxHeight: 560,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  scrollArea: {
    flex: 1,
    overflowY: "auto",
    paddingRight: 6,
    minHeight: 0,
  },

  panelHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 18,
    flexShrink: 0,
    flexWrap: "wrap",
    minWidth: 0,
  },

  panelEyebrow: {
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#6b7aa0",
    fontWeight: 800,
    marginBottom: 8,
  },

  panelTitle: {
    margin: 0,
    fontSize: "clamp(20px, 2vw, 28px)",
    lineHeight: 1.1,
    letterSpacing: "-0.03em",
    fontWeight: 900,
    color: "#112042",
    overflowWrap: "anywhere",
  },

  inlineMetric: {
    padding: "10px 14px",
    borderRadius: 16,
    background: "rgba(255,255,255,0.64)",
    border: "1px solid rgba(219,231,255,0.86)",
    minWidth: 100,
    textAlign: "right",
  },

  inlineMetricLabel: {
    display: "block",
    fontSize: 11,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#6b7aa0",
    fontWeight: 800,
    marginBottom: 4,
  },

  inlineMetricValue: {
    display: "block",
    fontSize: 24,
    lineHeight: 1,
    fontWeight: 900,
    color: "#2845d9",
  },


  lineChartCard: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
    borderRadius: 24,
    padding: "clamp(12px, 1.4vw, 18px)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.9), rgba(248,251,255,0.74))",
    border: "1px solid rgba(219,231,255,0.9)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
  },

  lineChartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 12,
    flexWrap: "wrap",
    minWidth: 0,
  },

  lineChartActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end",
    maxWidth: "100%",
    minWidth: 0,
  },

  lineLegend: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    fontSize: "clamp(10px, 0.9vw, 12px)",
    fontWeight: 800,
    color: "#667085",
    maxWidth: "100%",
  },

  rangeSelect: {
    minHeight: 42,
    minWidth: 142,
    borderRadius: 14,
    padding: "0 14px",
    border: "1px solid rgba(219,231,255,0.95)",
    background: "rgba(255,255,255,0.92)",
    color: "#213053",
    fontSize: 14,
    fontWeight: 900,
    outline: "none",
    boxShadow: "0 12px 26px rgba(17,32,66,0.06)",
  },

  lineChartAverage: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 10,
    marginBottom: 10,
  },

  lineChartAverageValue: {
    fontSize: "clamp(22px, 2vw, 28px)",
    lineHeight: 1,
    fontWeight: 900,
    color: "#112042",
    letterSpacing: "-0.04em",
  },

  lineChartAverageLabel: {
    fontSize: 13,
    color: "#667085",
    fontWeight: 800,
  },

  lineChartWrap: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    minHeight: 220,
    overflow: "hidden",
    borderRadius: 20,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.82), rgba(255,255,255,0.44))",
  },

  lineChartSvg: {
    width: "100%",
    maxWidth: "100%",
    height: "auto",
    minHeight: 220,
    maxHeight: 280,
    aspectRatio: "760 / 260",
    display: "block",
  },

  trendChartCard: {
  width: "100%",
  borderRadius: 24,
  padding: 18,
  background: "linear-gradient(180deg, rgba(255,255,255,0.88), rgba(248,251,255,0.72))",
  border: "1px solid rgba(219,231,255,0.9)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
},

trendChartHeader: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  marginBottom: 18,
},

trendChartTitle: {
  margin: 0,
  fontSize: 18,
  fontWeight: 900,
  color: "#112042",
},

trendLegend: {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  fontSize: 12,
  fontWeight: 800,
  color: "#667085",
},

trendPercent: {
  fontSize: 13,
  fontWeight: 900,
  color: "#112042",
},

trendBarTrack: {
  height: 160,
  width: "100%",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  borderRadius: 18,
  background: "linear-gradient(180deg, rgba(238,243,255,0.65), rgba(255,255,255,0.3))",
},

trendBar: {
  width: "100%",
  maxWidth: 38,
  borderRadius: 999,
  boxShadow: "0 14px 28px rgba(79,99,255,0.18)",
},

  trendPanelInner: {
    display: "block",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
  },

  trendMeta: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    height: "100%",
  },

  trendBigValue: {
    fontSize: 54,
    lineHeight: 1,
    fontWeight: 900,
    color: "#112042",
    letterSpacing: "-0.05em",
    marginBottom: 10,
  },

  trendBigSub: {
    fontSize: 15,
    lineHeight: 1.6,
    color: "#667085",
    maxWidth: 200,
  },

trendWrap: {
  minHeight: 170,
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  alignItems: "end",
  gap: 12,
  padding: "12px 0 2px",
},

  trendBarCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
  },

  trendBarLabel: {
    fontSize: 12,
    color: "#7a88a8",
    fontWeight: 700,
  },

  quickList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  quickAction: {
    border: "1px solid rgba(255,255,255,0.72)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(248,251,255,0.66) 100%)",
    borderRadius: 20,
    padding: "16px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 15,
    fontWeight: 800,
    color: "#213053",
    cursor: "pointer",
    boxShadow: "0 16px 32px rgba(17,32,66,0.06)",
  },

  quickArrow: {
    fontSize: 18,
    color: "#4f63ff",
    fontWeight: 900,
  },

  attentionList: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  attentionRow: {
    width: "100%",
    appearance: "none",
    border: "none",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "16px 16px",
    borderRadius: 20,
    textAlign: "left",
    cursor: "default",
    ...adminGlass.softRow,
  },

  attentionRowClickable: {
    cursor: "pointer",
    border: "1px solid rgba(254, 223, 137, 0.86)",
    boxShadow:
      "0 18px 36px rgba(181, 71, 8, 0.08), inset 0 1px 0 rgba(255,255,255,0.86)",
  },

  attentionLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  attentionDot: {
    width: 12,
    height: 12,
    borderRadius: 999,
    boxShadow: "0 0 0 6px rgba(79,99,255,0.06)",
    flexShrink: 0,
  },

  attentionLabel: {
    fontSize: 16,
    fontWeight: 800,
    color: "#15284b",
    marginBottom: 4,
  },

  attentionSub: {
    fontSize: 14,
    color: "#6b7a99",
    lineHeight: 1.45,
  },

  attentionActionText: {
    marginTop: 8,
    color: "#b54708",
    fontSize: 13,
    fontWeight: 950,
  },

  statusPill: {
    padding: "9px 12px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },

  activityList: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  activityRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    padding: "16px 16px",
    borderRadius: 20,
    alignItems: "center",
    ...adminGlass.softRow,
  },

  activityMeta: {
    minWidth: 0,
  },

  activityTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: "#15284b",
    marginBottom: 4,
  },

  activitySub: {
    fontSize: 14,
    color: "#6b7a99",
    lineHeight: 1.45,
  },

  activityTime: {
    flexShrink: 0,
    fontSize: 13,
    fontWeight: 800,
    color: "#4f63ff",
    background: "rgba(238,243,255,0.84)",
    border: "1px solid rgba(219,231,255,0.9)",
    borderRadius: 999,
    padding: "8px 10px",
  },

  emptyState: {
    padding: "18px 16px",
    borderRadius: 18,
    background: "rgba(255,255,255,0.58)",
    border: "1px solid rgba(219,231,255,0.86)",
    color: "#6b7a99",
    fontSize: 15,
  },
};